// 使用 import.meta.glob 预加载所有资源文件
const assetsModules = import.meta.glob('../assets/**/*.{png,jpg,jpeg,gif,svg,webp}', { eager: true })

// 引入 FingerprintJS
import FingerprintJS from '@fingerprintjs/fingerprintjs'

// 设备指纹相关
let fpInstance: any = null
const FINGERPRINT_STORAGE_KEY = 'device_fingerprint_fallback'

export const getAssetsFile = (url: string) => {
  const dir = import.meta.env.VITE_APP_ASSETS_DIR
  const fullPath = `../assets/${dir}/${url}`

  // 在开发环境中使用相对路径
  if (import.meta.env.DEV) {
    return new URL(fullPath, import.meta.url).href
  }

  // 在生产环境中，从预加载的模块中获取正确的文件路径
  // 首先尝试精确匹配完整路径
  const moduleKey = Object.keys(assetsModules).find(key => key.endsWith(`/${dir}/${url}`))

  if (moduleKey && assetsModules[moduleKey]) {
    // 返回 Vite 处理后的正确路径
    return (assetsModules[moduleKey] as any).default || assetsModules[moduleKey]
  }

  // 如果没找到，尝试通过文件名匹配（去掉目录结构）
  const fileName = url.split('/').pop()
  const fileNameWithoutExt = fileName?.split('.')[0]

  if (fileNameWithoutExt) {
    // 改进文件名匹配逻辑：查找包含文件名且扩展名匹配的文件
    const moduleKeyByFileName = Object.keys(assetsModules).find(key => {
      const keyFileName = key.split('/').pop()
      if (!keyFileName) return false

      // 检查文件名是否匹配（考虑 Vite 的哈希命名）
      const keyFileNameWithoutExt = keyFileName.split('.')[0]
      const keyExt = keyFileName.split('.').pop()
      const urlExt = fileName?.split('.').pop()

      return keyFileNameWithoutExt.includes(fileNameWithoutExt) && keyExt === urlExt
    })

    if (moduleKeyByFileName && assetsModules[moduleKeyByFileName]) {
      return (assetsModules[moduleKeyByFileName] as any).default || assetsModules[moduleKeyByFileName]
    }
  }

  // 最后的回退方案：返回基于 BASE_URL 的路径，只使用文件名
  const basePath = import.meta.env.BASE_URL || '/'
  return `${basePath}assets/${dir}/${fileName}`
}

/**
 * 从localStorage获取fallback指纹
 */
const getFallbackFromStorage = (): string | null => {
  try {
    return localStorage.getItem(FINGERPRINT_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to read from localStorage:', error)
    return null
  }
}

/**
 * 保存fallback指纹到localStorage
 */
const saveFallbackToStorage = (fingerprint: string): void => {
  try {
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint)
  } catch (error) {
    console.warn('Failed to save to localStorage:', error)
  }
}

/**
 * 生成新的fallback指纹
 */
const generateFallbackFingerprint = (): string => {
  const fallback = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  saveFallbackToStorage(fallback)
  return fallback
}

/**
 * 初始化 FingerprintJS 实例
 */
const initFingerprintJS = async () => {
  if (!fpInstance) {
    try {
      fpInstance = await FingerprintJS.load({
        monitoring: false,
      })
    } catch (error) {
      console.error('Failed to initialize FingerprintJS:', error)
      throw error
    }
  }
  return fpInstance
}

/**
 * 获取设备指纹
 * @returns {Promise<string>} 设备指纹ID
 */
export const getDeviceFingerprint = async (): Promise<string> => {
  try {
    const fp = await initFingerprintJS()
    const result = await fp.get()
    return result.visitorId
  } catch (error) {
    console.error('Failed to get device fingerprint:', error)

    // 优先从localStorage获取已存在的fallback指纹
    const existingFallback = getFallbackFromStorage()
    if (existingFallback) {
      console.log('Using existing fallback fingerprint from localStorage')
      return existingFallback
    }

    // 如果localStorage中没有，则生成新的fallback并保存
    console.log('Generating new fallback fingerprint')
    return generateFallbackFingerprint()
  }
}


/**
 * 清除localStorage中的fallback指纹（可选功能）
 */
export const clearFallbackFingerprint = (): void => {
  try {
    localStorage.removeItem(FINGERPRINT_STORAGE_KEY)
    console.log('Fallback fingerprint cleared from localStorage')
  } catch (error) {
    console.warn('Failed to clear fallback fingerprint from localStorage:', error)
  }
}

export const getCookie = (cname) => {
  var name = cname + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i].trim();
    if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
  }
  return "";
}

export const setCookie = (cname, cvalue, exdays = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  const expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

/**
 * 统一处理 URL 参数存储到 cookie
 * 如果 URL 中有参数，就存储到 cookie；如果 URL 中的参数和 cookie 中的不一样，就用 URL 中的最新值覆盖
 * @param paramNames 需要处理的参数名称数组，如 ['pixel', 'channelId', 'channelName']
 */
export const syncUrlParamsToCookie = (paramNames: string[] = ['pixel', 'channelId', 'channelName']) => {
  paramNames.forEach(paramName => {
    const urlValue = getUrlParam(paramName)
    if (urlValue) {
      // URL 中有这个参数，检查 cookie 中的值
      const cookieValue = getCookie(paramName)
      // 如果 URL 中的值和 cookie 中的值不一样，或者 cookie 中没有值，就用 URL 中的值覆盖
      if (urlValue !== cookieValue) {
        setCookie(paramName, urlValue)
        console.log(`[URL参数同步] ${paramName}: URL值(${urlValue}) 覆盖 Cookie值(${cookieValue || '无'})`)
      }
    }
  })
}

export const getUrlParam = (name) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * 下载APK
 */
export const downloadAPK = async () => {
  // 优先从 URL 参数获取，如果没有则从 cookie 获取
  let channelName = getUrlParam("channelName");
  if (channelName) {
    setCookie('channelName', channelName)
  } else {
    channelName = getCookie('channelName') || "mainChannel";
  }
  
  // 优先从 URL 参数获取 channelId，如果没有则从 cookie 获取
  let channelId = getUrlParam("channelId");
  if (channelId) {
    setCookie('channelId', channelId)
  } else {
    channelId = getCookie('channelId')
  }

  getDeviceFingerprint().then(deviceFingerprint => {
    let dowloadApkUrl = `${import.meta.env.VITE_BASE_URL}/app-api/download/channel/apk/index?domain=${window.location.hostname}&clientId=${deviceFingerprint}&channelName=${channelName}`;

    // 如果有 channelId，则添加到 URL 参数中
    if (channelId) {
      dowloadApkUrl += `&channelId=${channelId}`;
    }

    window.open(dowloadApkUrl, '_blank')
  })
}
